import React, {
  Suspense,
  useState,
  useEffect,
  ReactElement,
  ReactText,
} from "react";
import {
  Flex,
  BoxProps,
  Box,
  useColorModeValue,
  Text,
  CloseButton,
  FlexProps,
  Link,
  Drawer,
  DrawerContent,
  useDisclosure,
  VStack,
  IconButton,
  HStack,
  Menu,
  MenuButton,
  Avatar,
  MenuList,
  MenuItem,
  MenuDivider,
} from "@chakra-ui/react";
import { Redirect } from "react-router";
import { useLocation, useHistory, NavLink } from "react-router-dom";
import LoadingOverlay from "components/LoadingOverlay";
import { selectAuth } from "redux/slices/authSlice";
import { useTypedSelector } from "redux/hooks";
import {
  useGetProfileLazyQuery,
  useGetProfileQueryState,
  useGetProfileQuery,
  useCreateUserMutation,
} from "redux/services/membership";
import { FiMenu, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { RoutesLevel2, RoleOptions } from "common";
import Image from "components/Image";
import logo from "assets/images/rubix-logo.png";
import CreateEnterpriseUser from "containers/CreateEnterpriseUser";
import InviteOperationUser from "containers/InviteOperationUser";
import ChangePasswordModal from "containers/ChangePasswordModal";
import { GetProfileResponse } from "redux/services/membership/types";
import env from "env";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useGetUserRole } from "hooks/useGetUserRole";

interface PermittedRouteProps {
  label: string;
  icon: React.ReactElement;
  link: string;
  roles: string[];
  isActive?: boolean;
  children?: {
    label: string;
    icon: React.ReactElement;
    link: string;
    roles: string[];
    isActive?: boolean;
  }[];
}

interface NavItemProps extends FlexProps {
  icon: ReactElement;
  children: ReactText;
  link: string;
  isChild?: boolean;
  hasChildren?: boolean;
  childrenLink?: PermittedRouteProps[];
}

interface Props {
  children: React.ReactNode;
  forceAccessToken?: boolean;
}

interface SidebarProps extends BoxProps {
  permittedRoute: PermittedRouteProps[];
  profileData?: GetProfileResponse;
  onClose: () => void;
}

interface MobileProps extends FlexProps {
  onOpen: () => void;
}

const NavItem = ({
  icon,
  link,
  children,
  isChild,
  hasChildren,
  ...rest
}: NavItemProps) => {
  const location = useLocation();
  // Check if children menu is active
  const isChildrenActive = rest.childrenLink?.some(
    (link) => link.link === location.pathname
  );
  const [isOpen, setIsOpen] = useState(isChildrenActive);

  const toggleChildrenMenu = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevents link navigation
    setIsOpen(!isOpen);
  };

  return (
    <Box>
      <Link
        to={link}
        as={NavLink as any}
        style={{ textDecoration: "none" }}
        _focus={{ boxShadow: "none" }}
        _active={{
          "&>Flex": { color: "red !important" },
        }}
      >
        <Flex
          align="center"
          className={isChild ? "p-4 pl-14" : "p-4 pl-8"}
          role="group"
          cursor="pointer"
          color="#000"
          bg={
            link.split("?")[0] === location.pathname
              ? "linear-gradient(90deg, #e6f4fe 0%, #e6f4fe 100%)"
              : "transparent"
          }
          _hover={{
            bg: "linear-gradient(90deg, #e6f4fe 0%, #e6f4fe 100%);",
            color: "white",
          }}
          {...rest}
        >
          <Box
            flex="0 0 25px"
            width="25px"
            height="25px"
            maxWidth="25px"
            transform="translateY(-2px)"
          >
            {icon}
          </Box>
          <Text ml="10px" color="#000" lineHeight="1.5">
            {children}
          </Text>
          {hasChildren && (
            <Box
              ml="auto"
              fontSize="10px"
              color="gray.500"
              cursor="pointer"
              onClick={toggleChildrenMenu}
            >
              {!isOpen ? (
                <FiChevronDown color="black" fontSize={"12px"} />
              ) : (
                <FiChevronUp color="black" fontSize={"12px"} />
              )}
            </Box>
          )}
        </Flex>
      </Link>
      {hasChildren && isOpen && (
        <Box borderBottom={"1px"} borderBottomColor={"gray.200"}>
          <ul>
            {rest.childrenLink?.map((child) => (
              <li key={child.label}>
                <NavItem icon={child.icon} link={child.link} isChild={true}>
                  {child.label}
                </NavItem>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
};

const SidebarContent = ({
  onClose,
  permittedRoute,
  profileData,
  ...rest
}: SidebarProps) => {
  return (
    <Box
      transition="3s ease"
      bg={`linear-gradient(to right, #fff 0%, #fff 100%)`}
      borderRight="1px"
      borderRightColor={useColorModeValue("gray.200", "gray.700")}
      w={{ base: "full", md: 60 }}
      pos="fixed"
      h="full"
      {...rest}
    >
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Image src={logo} w="76px" h="50px" alt="101Digital" />
        <CloseButton display={{ base: "flex", md: "none" }} onClick={onClose} />
      </Flex>
      <ul>
        {permittedRoute
          .filter((link) => link.isActive)
          .map((link) => {
            let finalLink = link.link;
            const activeChildren = link.children
              ? link.children.filter((child) => child.isActive)
              : undefined;
            return (
              <li key={link.label}>
                <NavItem
                  icon={link.icon}
                  link={finalLink}
                  hasChildren={!!activeChildren && activeChildren.length > 0}
                  childrenLink={activeChildren}
                >
                  {link.label}
                </NavItem>
              </li>
            );
          })}
      </ul>
    </Box>
  );
};

const AuthLayout = ({ children, forceAccessToken }: Props) => {
  const { accessToken, refreshToken } = useTypedSelector(selectAuth);
  const { data } = useGetProfileQueryState();
  const { data: ProfileData } = useGetProfileQuery();
  const [trigger, { isSuccess, isError, error }] = useGetProfileLazyQuery();
  const [waitRequiredRequests, setWaitRequiredRequests] = useState(!data);
  const location = useLocation();
  const history = useHistory();
  const [routeData, setRouteData] = useState<any>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [createUser, { isSuccess: isCreateUserSuccess }] =
    useCreateUserMutation();

  const permittedRoute = () => {
    interface RouteItem {
      label: string;
      icon: React.ReactElement;
      link: string;
      roles: string[];
      children?: RouteItem[];
      isActive?: boolean;
    }

    const checkRoutePermissions = (routeItem: RouteItem): RouteItem => {
      // If no roles or empty roles array, route is active
      const hasNoRoles = !routeItem?.roles || routeItem?.roles?.length === 0;

      // Check if user has any of the required roles
      const isActive: boolean =
        hasNoRoles ||
        (ProfileData?.data?.listRoles?.some((r: string) =>
          routeItem?.roles?.includes(r)
        ) ??
          false);

      const finalPermission = {
        ...routeItem,
        isActive,
        ...(routeItem.children && {
          children: routeItem.children.map((child) =>
            checkRoutePermissions(child)
          ),
        }),
      };
      return finalPermission;
    };

    return RoutesLevel2.map(checkRoutePermissions);
  };
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  useEffect(() => {
    if (
      isError &&
      (error as any).status === 404 &&
      accessToken &&
      !isCreatingUser
    ) {
      setIsCreatingUser(true);
      const parseToken = accessToken.split(".")[1];
      const jsonToken = JSON.parse(atob(parseToken));
      const adRoles = env.app.adRoles.split(",");
      const jsonRoles = jsonToken.roles || jsonToken["cognito:groups"];
      // Check if jsonRoles is a string
      if (typeof jsonRoles === "string") {
        jsonToken.roles = [jsonRoles];
      } else {
        jsonToken.roles = jsonRoles;
      }
      if (
        adRoles.includes(jsonToken.roles) ||
        jsonToken.roles.some((role: string) => adRoles.includes(role))
      ) {
        createUser({
          emailAddress: jsonToken.email,
          firstName: jsonToken.given_name,
          lastName: jsonToken.family_name,
          userId: jsonToken.externalidpid,
          listRoles: jsonToken.roles.filter(
            (role: string) => !role.includes("_Azure")
          ),
          userName: jsonToken.userId,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error, accessToken, isCreatingUser]);

  useEffect(() => {
    if (waitRequiredRequests || isCreateUserSuccess) {
      trigger();
    }
  }, [waitRequiredRequests, isCreateUserSuccess, trigger]);

  useEffect(() => {
    if (!waitRequiredRequests) {
      const route = RoutesLevel2.filter((item) =>
        item.roles.some((role) => ProfileData?.data?.listRoles.includes(role))
      );
      // check if the url entered is permitted for the user.(prevent manual url entering)
      setRouteData(route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location,
    waitRequiredRequests,
    history,
    data?.data.listRoles,
    ProfileData,
  ]);

  useEffect(() => {
    if (isSuccess) setWaitRequiredRequests(false);
  }, [isSuccess]);

  if (!accessToken && !forceAccessToken && !refreshToken)
    return (
      <Redirect to={{ pathname: "/", state: { from: location.pathname } }} />
    );

  return waitRequiredRequests && !forceAccessToken ? (
    <LoadingOverlay h="100%" />
  ) : (
    <Box minH="100vh" position="relative">
      <SidebarContent
        profileData={ProfileData}
        permittedRoute={permittedRoute()}
        onClose={onClose}
        display={{ base: "none", md: "block" }}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent
            profileData={ProfileData}
            permittedRoute={permittedRoute()}
            onClose={onClose}
          />
        </DrawerContent>
      </Drawer>

      <MobileNav onOpen={onOpen} />

      <Box ml={{ base: 0, md: 60 }} height="calc(100vh - 80px)">
        <Box>
          <Box display="block" p={"20px"} position={"relative"}>
            <Flex
              borderRadius="10px 10px 10px 10px"
              bg="white"
              w="100%"
              borderWidth="1px"
            >
              <Suspense fallback={<LoadingOverlay isSkeleton />}>
                {children}
              </Suspense>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MobileNav = ({ onOpen, ...rest }: MobileProps) => {
  const history = useHistory();
  const { data: ProfileData } = useGetProfileQuery();

  const onLogout = () => {
    window.location.href = env.api.baseUrl.logoutUrl;
  };
  const { accessToken } = useTypedSelector(selectAuth);

  const parseToken = accessToken.split(".")[1];
  let jsonToken = {
    given_name: "Guest",
    family_name: "User",
  };
  try {
    jsonToken = JSON.parse(atob(parseToken));
  } catch (e) {
    console.error("Failed to parse access token:", e);
    jsonToken = { given_name: "Guest", family_name: "User" }; // Fallback for error
  }
  const {
    isEnterpriseBankAdmin,
    isEnterpriseAdmin,
    isEnterpriseSalesTeamMember,
    isEnterpriseDeveloper,
    isEnterprisePaymentOperation,
  } = useGetUserRole();

  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalInviteOpts, setOpenModalInviteOpts] = useState(false);
  const [openModalChangePassword, setOpenModalChangePassword] = useState(false);

  return (
    <div className="top-0 left-0 z-50 w-full sticky">
      <Flex
        ml={{ base: 0, md: 60 }}
        px={{ base: 4, md: 4 }}
        height="20"
        alignItems="center"
        bg={useColorModeValue("white", "gray.900")}
        borderBottom={"1px"}
        borderBottomColor={"gray.200"}
        justifyContent={{ base: "space-between", md: "flex-end" }}
        {...rest}
      >
        <Flex>
          <Link
            href={env.app.apiCafeUrl}
            isExternal
            display={"flex"}
            width={"12vw"}
            alignItems={"center"}
          >
            API Document Portal <ExternalLinkIcon mx="2px" />
          </Link>
        </Flex>

        <IconButton
          display={{ base: "flex", md: "none" }}
          onClick={onOpen}
          variant="outline"
          aria-label="open menu"
          icon={<FiMenu />}
        />
        <HStack
          spacing={{ base: "0", md: "12" }}
          w="100%"
          justifyContent="space-between"
        >
          <Flex alignItems="center" ml={["20px", "20px", "unset"]} />

          <Flex alignItems={"center"}>
            <Menu>
              <HStack>
                <VStack
                  display={{ base: "none", md: "flex" }}
                  alignItems="flex-end"
                  spacing="5px"
                  ml="2"
                >
                  <Text fontSize="sm">
                    {ProfileData?.data?.firstName ?? jsonToken.given_name}{" "}
                    {ProfileData?.data?.lastName ?? jsonToken.family_name} (
                    {RoleOptions.find((option) =>
                      ProfileData?.data?.listRoles?.includes(option.value)
                    )?.label || "Enterprise User"}
                    )
                  </Text>
                  <a
                    className="underline text-blue-500"
                    href="/documentation"
                    rel="noreferrer"
                    target="_blank"
                  >
                    Enterprise Payments User Guide
                  </a>
                </VStack>
                <MenuButton
                  py={2}
                  transition="all 0.3s"
                  _focus={{ boxShadow: "none" }}
                >
                  <VStack
                    display={{ base: "none", md: "flex" }}
                    alignItems="center"
                    spacing="1px"
                    flexDirection="row"
                    gap={"5px"}
                    ml="2"
                  >
                    <Avatar
                      size={"sm"}
                      bg="#EBEBEB"
                      name={`${ProfileData?.data?.firstName} ${ProfileData?.data?.lastName}`}
                    />
                    <Box display={{ base: "none", md: "flex" }}>
                      <FiChevronDown />
                    </Box>{" "}
                  </VStack>
                </MenuButton>
              </HStack>

              <MenuList
                bg={useColorModeValue("white", "gray.900")}
                borderColor={useColorModeValue("gray.200", "gray.700")}
              >
                {(isEnterpriseAdmin || isEnterpriseDeveloper) && (
                  <MenuItem
                    onClick={() => {
                      history.push("/myAccount");
                    }}
                  >
                    {"My Account"}
                  </MenuItem>
                )}

                {(isEnterpriseBankAdmin ||
                  isEnterpriseAdmin ||
                  isEnterpriseSalesTeamMember) && (
                  <MenuItem
                    onClick={() => {
                      setOpenModalCreate(true);
                    }}
                  >
                    {isEnterpriseBankAdmin || isEnterpriseSalesTeamMember
                      ? "Register TPPP User"
                      : "Invite Collaborator"}
                  </MenuItem>
                )}
                <MenuDivider />
                {!isEnterpriseBankAdmin &&
                  !isEnterpriseSalesTeamMember &&
                  !isEnterprisePaymentOperation && (
                    <MenuItem
                      onClick={() => {
                        setOpenModalChangePassword(true);
                      }}
                    >
                      Change Password
                    </MenuItem>
                  )}

                <MenuItem
                  onClick={() => {
                    onLogout();
                  }}
                >
                  Sign out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </HStack>
        {openModalCreate && (
          <CreateEnterpriseUser
            isOpen={openModalCreate}
            setOpenModalCreate={() => setOpenModalCreate(false)}
          />
        )}
        {openModalInviteOpts && (
          <InviteOperationUser
            isOpen={openModalInviteOpts}
            setOpenModalCreate={() => setOpenModalInviteOpts(false)}
          />
        )}
        <ChangePasswordModal
          visible={openModalChangePassword}
          onClose={() => setOpenModalChangePassword(false)}
        />
      </Flex>
    </div>
  );
};

export default AuthLayout;
